import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
  alpha,
  styled,
  useTheme
} from "@mui/material";

import { useEffect, useMemo, useRef, useState } from "react";

import { refreshDashboardStats } from "@/api/dashboard/refresh/actions";
import { applyIndustryTemplate, getIndustryTemplates } from "@/api/industryMeta/actions";
import {
  type SignupProvisioningContent,
  type SignupProvisioningStep,
  isSignupProvisioningReady
} from "@/api/signup/provisioning";
import { addSampleDataToTenant, patchTenant, updateTenant } from "@/api/tenants/actions";
import CButton from "@/components/Button";
import { logger } from "@/lib/logger";
import { isSignupSetupRoute } from "@/lib/signup/setupRouting";
import type { User } from "@/models/user";
import { update as updateAuthUser } from "@/slices/auth/authReducer";
import { useDispatch, useSelector } from "@/store";
import { rootReducerName } from "@/store/rootReducer";
import type { TFunction } from "i18next";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

const I18N_PAGE_NS = "pages/signup/setup";

type AuthUser = {
  tenantGuid?: string;
  tenantDisplayString?: string;
  tenant_config?: { personalization_overlay_completed?: boolean } | null;
  tenantConfig?: { personalization_overlay_completed?: boolean } | null;
  tenant_stats?: Record<string, unknown> | null;
  industry?: string | null;
  firstLogin?: boolean;
  name?: string;
  email?: string;
  accountGuid?: string;
  spAId?: string;
  spUserGuid?: string;
  id?: string;
  pendingWorkspaceSelection?: boolean;
  spPendingWorkspaceSelection?: boolean;
};

type InitPhase = "idle" | "apply_template" | "update_tenant" | "start_sample_data" | "refresh_dashboard";
type ProvisioningStepStatus = "complete" | "current" | "pending" | "failed" | "skipped";

type IndustryTemplate = {
  id: string;
  name?: string;
  sample_data?: {
    default_counts?: Record<string, number>;
  };
  sample_data_hints?: {
    default_counts?: Record<string, number>;
  };
};

interface SignupPersonalizationStepProps {
  mode: "inline" | "dialog";
  open?: boolean;
  onComplete?: () => void | Promise<void>;
  onDismiss?: () => void;
  workspaceGuid?: string;
  workspaceDisplayString?: string;
  provisioning?: SignupProvisioningContent | null;
  provisioningError?: string | null;
  provisioningRefreshing?: boolean;
  provisioningRetrying?: boolean;
  onRefreshProvisioning?: () => void;
  onRetryProvisioning?: () => void;
}

const Shell = styled(Box)(
  ({ theme }) => `
  padding: ${theme.spacing(5)};
  text-align: left;
  background: ${theme.palette.background.paper};
  border-radius: ${theme.shape.borderRadius}px;
`
);

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const phaseLabel = (t: TFunction, phase: InitPhase): string => {
  switch (phase) {
    case "apply_template":
      return t("Applying industry template");
    case "update_tenant":
      return t("Saving your settings");
    case "start_sample_data":
      return t("Starting sample data generation");
    case "refresh_dashboard":
      return t("Refreshing dashboard");
    default:
      return "";
  }
};

const normalizeIndustryList = (value: unknown): IndustryTemplate[] => {
  if (Array.isArray(value)) {
    return value as IndustryTemplate[];
  }

  const maybeContent = value as { content?: unknown } | null | undefined;
  if (maybeContent && Array.isArray(maybeContent.content)) {
    return maybeContent.content as IndustryTemplate[];
  }

  return [];
};

const getTemplateDefaults = (template?: IndustryTemplate | null): Record<string, number> => {
  return template?.sample_data?.default_counts || template?.sample_data_hints?.default_counts || {};
};

const getCompletedTenantConfig = (user: AuthUser | null) => {
  const currentTenantConfig = user?.tenant_config || user?.tenantConfig || null;
  return {
    ...(currentTenantConfig && typeof currentTenantConfig === "object" ? currentTenantConfig : {}),
    personalization_overlay_completed: true
  };
};

const normalizeProvisioningText = (value?: string | null): string => {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
};

const formatProvisioningStepLabel = (t: TFunction, stepKey: string, stepIndex: number): string => {
  const normalized = normalizeProvisioningText(stepKey).replace(/_/g, " ");
  if (!normalized) {
    return t("Step {{number}}", { number: stepIndex + 1 });
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const normalizeProvisioningStatus = (value?: string | null): string => {
  return String(value || "")
    .trim()
    .toLowerCase();
};

const getProvisioningStepStatus = (status: string): ProvisioningStepStatus => {
  const normalized = normalizeProvisioningStatus(status);
  if (normalized === "ready") return "complete";
  if (normalized === "running") return "current";
  if (normalized === "skipped") return "skipped";
  if (normalized.startsWith("failed")) return "failed";
  return "pending";
};

type ProvisioningStepView = {
  index: number;
  total: number;
  stepKey: string;
  label: string;
  status: ProvisioningStepStatus;
  message: string;
  rawStatus: string;
  isCurrent: boolean;
  isCompleted: boolean;
  isFailed: boolean;
};

type ProvisioningProgressView = {
  steps: ProvisioningStepView[];
  totalSteps: number;
  completedSteps: number;
  currentStepPosition: number | null;
  currentStepLabel: string;
  failureSummary: string | null;
  failureDetails: string | null;
  determinateValue: number | null;
  hasDetailedProgress: boolean;
};

const buildProvisioningProgressView = (
  t: TFunction,
  content?: SignupProvisioningContent | null
): ProvisioningProgressView | null => {
  const rawSteps = Array.isArray(content?.steps) ? (content.steps as SignupProvisioningStep[]) : [];
  if (rawSteps.length === 0) {
    return null;
  }

  const currentStepKey = normalizeProvisioningText(content?.current_step);
  const normalizedStatus = normalizeProvisioningStatus(content?.status);
  const normalizedSteps = rawSteps.map((step, index) => {
    const stepKey = normalizeProvisioningText(step?.step_key);
    const label = normalizeProvisioningText(step?.step_label) || formatProvisioningStepLabel(t, stepKey, index);
    const rawStatus = normalizeProvisioningStatus(step?.status);
    return {
      index,
      total: rawSteps.length,
      stepKey,
      label,
      status: getProvisioningStepStatus(rawStatus),
      message: normalizeProvisioningText(step?.message || step?.last_error),
      rawStatus,
      isCurrent: false,
      isCompleted: false,
      isFailed: false
    };
  });

  let currentIndex = normalizedSteps.findIndex((step) => step.stepKey && step.stepKey === currentStepKey);
  if (currentIndex < 0) {
    currentIndex = normalizedSteps.findIndex((step) => step.rawStatus === "running");
  }
  const failureIndex = normalizedSteps.findIndex((step) => step.rawStatus.startsWith("failed"));
  if (currentIndex < 0 && failureIndex >= 0) {
    currentIndex = failureIndex;
  }
  if (
    currentIndex < 0 &&
    (normalizedStatus === "ready" || normalizedStatus === "completed") &&
    normalizedSteps.length > 0
  ) {
    currentIndex = normalizedSteps.length - 1;
  }
  if (currentIndex < 0 && currentStepKey && normalizedSteps.length > 0) {
    const nextPendingIndex = normalizedSteps.findIndex((step) => step.status === "pending");
    currentIndex = nextPendingIndex >= 0 ? nextPendingIndex : normalizedSteps.length - 1;
  }
  if (currentIndex < 0 && normalizedSteps.length > 0) {
    currentIndex = 0;
  }

  const currentPosition = currentIndex >= 0 ? currentIndex + 1 : null;
  const completedSteps = normalizedSteps.filter((step, index) => {
    if (step.status === "complete" || step.status === "skipped") {
      return true;
    }
    if (currentIndex >= 0 && index < currentIndex && step.status === "pending") {
      return true;
    }
    return false;
  }).length;
  const totalSteps = normalizedSteps.length;
  const currentStep = currentIndex >= 0 ? normalizedSteps[currentIndex] : null;
  const failureStep = failureIndex >= 0 ? normalizedSteps[failureIndex] : null;
  const failureSummary = failureStep
    ? t("Step {{current}}/{{total}} failed: {{label}}", {
        current: failureStep.index + 1,
        total: totalSteps,
        label: failureStep.label
      })
    : normalizeProvisioningStatus(content?.status).startsWith("failed") && currentStep
      ? t("Step {{current}}/{{total}} failed: {{label}}", {
          current: currentStep.index + 1,
          total: totalSteps,
          label: currentStep.label
        })
      : null;
  const failureDetails = failureStep?.message || normalizeProvisioningText(content?.message) || null;

  return {
    steps: normalizedSteps.map((step, index) => {
      const isCurrent = index === currentIndex && step.status !== "complete" && step.status !== "skipped";
      const isFailed = step.status === "failed";
      const isCompleted =
        step.status === "complete" ||
        step.status === "skipped" ||
        (currentIndex >= 0 && index < currentIndex && step.status === "pending");
      const effectiveStatus: ProvisioningStepStatus = isFailed
        ? "failed"
        : isCurrent
          ? "current"
          : isCompleted
            ? "complete"
            : step.status;
      return {
        ...step,
        status: effectiveStatus,
        isCurrent,
        isCompleted,
        isFailed
      };
    }),
    totalSteps,
    completedSteps,
    currentStepPosition: currentPosition,
    currentStepLabel: currentStep?.label || t("Preparing your workspace"),
    failureSummary,
    failureDetails,
    determinateValue:
      totalSteps > 0 ? Math.max(0, Math.min(100, Math.round((completedSteps / totalSteps) * 100))) : null,
    hasDetailedProgress: totalSteps > 0
  };
};

const getProvisioningStatusLabel = (t: TFunction, status: ProvisioningStepStatus): string => {
  switch (status) {
    case "complete":
      return t("Done");
    case "current":
      return t("In progress");
    case "pending":
      return t("Waiting");
    case "failed":
      return t("Problem");
    case "skipped":
      return t("Skipped");
    default:
      return "";
  }
};

const provisioningStatusColor: Record<ProvisioningStepStatus, "success" | "primary" | "default" | "error" | "warning"> =
  {
    complete: "success",
    current: "primary",
    pending: "default",
    failed: "error",
    skipped: "warning"
  };

const getProvisioningFailureSummary = (t: TFunction, content?: SignupProvisioningContent | null): string | null => {
  const status = normalizeProvisioningStatus(content?.status);
  if (!status.startsWith("failed")) {
    return null;
  }

  const stepKey = normalizeProvisioningText(content?.current_step);
  if (!stepKey) {
    return t("Provisioning failed");
  }

  return t("Provisioning failed: {{step}}", { step: formatProvisioningStepLabel(t, stepKey, 0) });
};

const shouldShowProvisioningGuide = (content?: SignupProvisioningContent | null): boolean => {
  if (!content || isSignupProvisioningReady(content)) {
    return false;
  }

  const status = normalizeProvisioningStatus(content.status);
  return status === "pending" || status === "running";
};

const getProvisioningGuideItems = (t: TFunction) =>
  [
    {
      title: t("Choose your industry"),
      body: t("We will tailor terminology and starter settings to the way your team works.")
    },
    {
      title: t("Decide on sample data"),
      body: t("Add an example workspace if you want something useful to explore on your first visit.")
    },
    {
      title: t("Enter when access is ready"),
      body: t("We will confirm permissions before you continue. Optional content can finish in the background.")
    }
  ] as const;

function ProvisioningWaitGuide({ visible }: { visible: boolean }) {
  const { t } = useTranslation([I18N_PAGE_NS, "common"]);
  const theme = useTheme();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!visible) {
      setDismissed(false);
    }
  }, [visible]);

  if (!visible || dismissed) {
    return null;
  }

  return (
    <Paper
      component="section"
      role="region"
      aria-label={t("Workspace setup guide")}
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2,
        borderColor: (currentTheme) => alpha(currentTheme.palette.secondary.main, 0.18),
        bgcolor: alpha(theme.palette.secondary.main, 0.04)
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Chip label={t("While we prepare access")} color="secondary" size="small" />
            <Typography component="h3" variant="h5" sx={{ mt: 1, fontWeight: 800 }}>
              {t("Make the workspace yours while we finish")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {t(
                "Required permissions are being prepared. You can personalize the workspace now instead of waiting on a loading screen."
              )}
            </Typography>
          </Box>
          <IconButton aria-label={t("Dismiss workspace setup guide")} size="small" onClick={() => setDismissed(true)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 1.25
          }}
        >
          {getProvisioningGuideItems(t).map((item, index) => (
            <Box
              key={item.title}
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.background.paper, 0.82),
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.14)}`
              }}
            >
              <Typography variant="caption" color="secondary.main" sx={{ fontWeight: 800 }}>
                {index + 1}
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 0.25, fontWeight: 800 }}>
                {item.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Stack>
    </Paper>
  );
}

function ProvisioningProgressPanel({
  content,
  progress,
  provisioningError,
  provisioningRefreshing,
  provisioningRetrying,
  onRefreshProvisioning,
  onRetryProvisioning
}: {
  content: SignupProvisioningContent | null | undefined;
  progress: ProvisioningProgressView | null;
  provisioningError?: string | null;
  provisioningRefreshing?: boolean;
  provisioningRetrying?: boolean;
  onRefreshProvisioning?: () => void;
  onRetryProvisioning?: () => void;
}) {
  const { t } = useTranslation([I18N_PAGE_NS, "common"]);
  const theme = useTheme();
  const [showDetails, setShowDetails] = useState(false);
  const status = normalizeProvisioningStatus(content?.status);
  const isComplete = isSignupProvisioningReady(content);
  const isFailed = status.startsWith("failed");
  const failureDetails = progress?.failureDetails || normalizeProvisioningText(content?.message) || null;
  const hasProvisioningError = Boolean(provisioningError);
  const isRetryableFailure = isFailed && (Boolean(content?.retryable) || status === "failed_retryable");
  const showRetryAction = Boolean(onRetryProvisioning && isRetryableFailure);
  const showRefreshAction = Boolean(onRefreshProvisioning && hasProvisioningError && !isRetryableFailure);
  const shouldShowFailureDetails = isFailed || hasProvisioningError;

  if (!content && !progress && !hasProvisioningError) {
    return null;
  }

  const hasStepDetails = Boolean(progress?.steps.length);
  const hasAdditionalDetails = hasStepDetails || shouldShowFailureDetails;
  const headline = isComplete
    ? t("Your workspace is ready")
    : isFailed
      ? t("Setup needs attention")
      : hasProvisioningError
        ? t("Checking workspace status")
        : t("Getting your workspace ready");
  const statusLabel: string = isComplete
    ? t("Ready")
    : isFailed
      ? t("Needs attention")
      : progress?.currentStepPosition && progress.totalSteps
        ? t("Step {{current}} of {{total}}", {
            current: progress.currentStepPosition,
            total: progress.totalSteps
          })
        : hasProvisioningError
          ? t("Loading status")
          : t("In progress");
  const statusColor: "success" | "error" | "primary" | "warning" = isComplete
    ? "success"
    : isFailed
      ? "error"
      : hasProvisioningError
        ? "warning"
        : "primary";
  const summaryText = isComplete
    ? t("Everything is ready.")
    : isFailed
      ? progress?.currentStepPosition && progress.totalSteps
        ? t("{{completed}} of {{total}} steps complete. Open the setup steps to see what needs attention.", {
            completed: progress.completedSteps,
            total: progress.totalSteps
          })
        : t("We ran into a problem while setting up your workspace.")
      : progress
        ? t("{{completed}} of {{total}} steps complete.", {
            completed: progress.completedSteps,
            total: progress.totalSteps
          })
        : hasProvisioningError
          ? t("We could not load the latest setup status right now.")
          : t("We’re setting things up in the background.");
  const detailToggleLabel = hasStepDetails ? t("Show setup steps") : t("Show details");

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        textAlign: "left",
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {headline}
            </Typography>
            {Boolean(progress) || (!isFailed && !hasProvisioningError) ? (
              <Typography variant="body2" color="text.secondary">
                {summaryText}
              </Typography>
            ) : null}
          </Box>
          <Chip size="small" color={statusColor} variant="outlined" label={statusLabel} />
        </Stack>

        {isFailed ? (
          <Alert severity="error" variant="outlined">
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {progress?.currentStepPosition && progress.totalSteps
                ? t("Step {{current}} of {{total}} needs attention.", {
                    current: progress.currentStepPosition,
                    total: progress.totalSteps
                  })
                : t("We ran into a problem while setting up your workspace.")}
            </Typography>
          </Alert>
        ) : null}

        {!isFailed && hasProvisioningError ? (
          <Alert severity="warning" variant="outlined">
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {t("We could not load the latest setup status right now.")}
            </Typography>
          </Alert>
        ) : null}

        {progress ? (
          <>
            <LinearProgress
              variant="determinate"
              value={progress.determinateValue ?? 0}
              sx={{ height: 8, borderRadius: 999 }}
            />
            <Typography variant="caption" color="text.secondary">
              {isComplete
                ? t("You can continue to the app now.")
                : t("You can keep using this page while we finish in the background.")}
            </Typography>
          </>
        ) : !isComplete ? (
          <LinearProgress />
        ) : null}

        {hasAdditionalDetails ? (
          <Button
            size="small"
            variant="text"
            onClick={() => setShowDetails((current) => !current)}
            aria-expanded={showDetails}
            aria-controls="signup-provisioning-details"
            sx={{ alignSelf: "flex-start", px: 0, textTransform: "none", fontWeight: 700 }}
          >
            {showDetails ? t("Hide setup steps") : detailToggleLabel}
          </Button>
        ) : null}

        <Collapse in={showDetails && hasAdditionalDetails} timeout="auto" unmountOnExit>
          <Stack id="signup-provisioning-details" spacing={1}>
            {progress ? <ProvisioningStepDetails progress={progress} /> : null}
            {!progress && shouldShowFailureDetails ? (
              <Alert severity={isFailed ? "error" : "warning"} variant="outlined">
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {failureDetails || provisioningError || t("We ran into a problem while setting up your workspace.")}
                </Typography>
              </Alert>
            ) : null}
          </Stack>
        </Collapse>

        {showRetryAction || showRefreshAction ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            {showRetryAction ? (
              <CButton
                variant="outlined"
                onClick={() => onRetryProvisioning?.()}
                loading={Boolean(provisioningRetrying)}
                startIcon={<ReplayIcon fontSize="small" />}
              >
                {t("Retry provisioning")}
              </CButton>
            ) : null}
            {showRefreshAction ? (
              <CButton
                variant="outlined"
                onClick={() => onRefreshProvisioning?.()}
                loading={Boolean(provisioningRefreshing)}
                startIcon={<ReplayIcon fontSize="small" />}
              >
                {t("Check status again")}
              </CButton>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}

function ProvisioningStepDetails({ progress }: { progress: ProvisioningProgressView }) {
  const { t } = useTranslation([I18N_PAGE_NS, "common"]);
  const theme = useTheme();

  return (
    <Stack spacing={1}>
      {progress.steps.map((step) => (
        <Box
          key={`${step.stepKey || "step"}-${step.index}`}
          sx={{
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: step.isCurrent ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.action.hover, 0.04),
            border: `1px solid ${
              step.isFailed
                ? alpha(theme.palette.error.main, 0.36)
                : step.isCurrent
                  ? alpha(theme.palette.primary.main, 0.26)
                  : alpha(theme.palette.divider, 0.9)
            }`
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: step.isCurrent ? 700 : 600 }}>
                {step.index + 1}. {step.label}
              </Typography>
              {step.message ? (
                <Typography variant="caption" color="text.secondary">
                  {step.message}
                </Typography>
              ) : null}
            </Box>
            <Chip
              size="small"
              color={provisioningStatusColor[step.status]}
              label={getProvisioningStatusLabel(t, step.status)}
              variant={step.isCurrent ? "filled" : "outlined"}
            />
          </Stack>
        </Box>
      ))}
      {progress.failureSummary ? (
        <Alert severity="error" variant="outlined">
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {progress.failureSummary}
          </Typography>
          {progress.failureDetails ? (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {progress.failureDetails}
            </Typography>
          ) : null}
        </Alert>
      ) : null}
    </Stack>
  );
}

function PersonalizationForm({
  loading,
  phase,
  error,
  industries,
  selectedIndustry,
  industriesReady,
  useSampleData,
  mode,
  onSelectIndustry,
  onToggleSampleData,
  onSubmit,
  onDismiss
}: {
  loading: boolean;
  phase: InitPhase;
  error: string;
  industries: IndustryTemplate[];
  selectedIndustry: string;
  industriesReady: boolean;
  useSampleData: boolean;
  mode: "inline" | "dialog";
  onSelectIndustry: (value: string) => void;
  onToggleSampleData: (value: boolean) => void;
  onSubmit: () => void;
  onDismiss?: () => void;
}) {
  const { t } = useTranslation([I18N_PAGE_NS, "common"]);
  const theme = useTheme();

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography component="h2" variant={mode === "inline" ? "h5" : "h3"} sx={{ fontWeight: 800 }}>
          {t("Tailor to your business")}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t("Pick an industry and whether to add sample data. Finish this step to start using the app.")}
        </Typography>
      </Stack>

      <Box textAlign="left">
        <InputLabel sx={{ mb: 1, fontWeight: "bold" }}>{t("Choose your industry")}</InputLabel>
        <FormControl fullWidth>
          <Select
            value={selectedIndustry}
            onChange={(event) => onSelectIndustry(String(event.target.value))}
            disabled={loading}
          >
            {industries.map((industry) => (
              <MenuItem key={industry.id} value={industry.id}>
                {industry.name || industry.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box
        sx={{
          p: 2,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
          textAlign: "left"
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={useSampleData}
              onChange={(event) => onToggleSampleData(event.target.checked)}
              disabled={loading}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {t("Add sample data")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("Recommended if you want to explore the full product right away.")}
              </Typography>
            </Box>
          }
        />
      </Box>

      {error ? (
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ textAlign: "left" }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
            {phaseLabel(t, phase)}
          </Typography>
          <LinearProgress />
        </Box>
      ) : null}

      <CButton
        fullWidth
        variant="contained"
        size="large"
        onClick={onSubmit}
        disabled={loading || !industriesReady || industries.length === 0}
        sx={{
          py: 1.5,
          fontSize: "1.05rem",
          fontWeight: "bold",
          boxShadow: theme.shadows[10]
        }}
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
      >
        {loading
          ? phaseLabel(t, phase) || t("Completing setup...")
          : industriesReady
            ? t("Finish setup")
            : t("Loading setup options...")}
      </CButton>

      {mode === "dialog" && onDismiss ? (
        <Button variant="text" color="inherit" onClick={onDismiss} sx={{ textTransform: "none" }}>
          {t("I'll do this later")}
        </Button>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t("We'll keep working in the background while you finish this step.")}
        </Typography>
      )}
    </Stack>
  );
}

export default function SignupPersonalizationStep({
  mode,
  open = true,
  onComplete,
  onDismiss,
  workspaceGuid,
  workspaceDisplayString,
  provisioning,
  provisioningError,
  provisioningRefreshing,
  provisioningRetrying,
  onRefreshProvisioning,
  onRetryProvisioning
}: SignupPersonalizationStepProps) {
  const { t } = useTranslation([I18N_PAGE_NS, "common"]);
  const router = useRouter();
  const dispatch = useDispatch();
  const user = useSelector((state) => state[rootReducerName.auth]?.user) as AuthUser | null;
  const [industries, setIndustries] = useState<IndustryTemplate[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [useSampleData, setUseSampleData] = useState(true);
  const [industriesReady, setIndustriesReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<InitPhase>("idle");
  const [error, setError] = useState("");
  const industriesRef = useRef<IndustryTemplate[]>([]);
  const loggedProvisioningFailureRef = useRef<string | null>(null);
  const resolvedTenantGuid =
    normalizeProvisioningText(provisioning?.tenant_id) ||
    normalizeProvisioningText(user?.tenantGuid) ||
    normalizeProvisioningText(workspaceGuid);
  const resolvedTenantDisplayString =
    normalizeProvisioningText(provisioning?.tenant_display_string) ||
    normalizeProvisioningText(user?.tenantDisplayString) ||
    normalizeProvisioningText(workspaceDisplayString);

  const isSetupRoute = isSignupSetupRoute(router.asPath || router.pathname);
  const shouldRender = mode === "inline" || open;
  const provisioningProgress = useMemo(() => buildProvisioningProgressView(t, provisioning), [provisioning, t]);
  const provisioningFailureSummary = useMemo(
    () => provisioningProgress?.failureSummary || getProvisioningFailureSummary(t, provisioning),
    [provisioning, provisioningProgress?.failureSummary, t]
  );
  const provisioningFailureDetails = useMemo(
    () => provisioningProgress?.failureDetails || normalizeProvisioningText(provisioning?.message) || null,
    [provisioning?.message, provisioningProgress?.failureDetails]
  );
  const showProvisioningGuide = mode === "inline" && shouldShowProvisioningGuide(provisioning);

  useEffect(() => {
    const failureSummary = provisioningFailureSummary;
    if (!failureSummary) {
      loggedProvisioningFailureRef.current = null;
      return;
    }

    if (loggedProvisioningFailureRef.current === failureSummary) {
      return;
    }

    loggedProvisioningFailureRef.current = failureSummary;
    logger.warn("SignupPersonalizationStep: provisioning failed", {
      currentStepLabel: provisioningProgress?.currentStepLabel,
      currentStepPosition: provisioningProgress?.currentStepPosition,
      failureDetails: provisioningFailureDetails,
      failureSummary,
      totalSteps: provisioningProgress?.totalSteps
    });
  }, [
    provisioningFailureDetails,
    provisioningFailureSummary,
    provisioningProgress?.currentStepLabel,
    provisioningProgress?.currentStepPosition,
    provisioningProgress?.totalSteps
  ]);

  useEffect(() => {
    if (!shouldRender) {
      return;
    }

    let cancelled = false;
    void getIndustryTemplates()
      .then((result: any) => {
        if (cancelled) {
          return;
        }
        const data = normalizeIndustryList(result);
        industriesRef.current = data;
        setIndustries(data);
        setSelectedIndustry((current) => current || data[0]?.id || "");
        setIndustriesReady(true);
      })
      .catch((err) => {
        if (!cancelled) {
          logger.warn("SignupPersonalizationStep: failed to load industries", { err });
          setError(t("Unable to load industries right now. You can try again in a moment."));
          setIndustriesReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [shouldRender]);

  useEffect(() => {
    if (!mode || mode !== "dialog" || !isSetupRoute) {
      return;
    }

    if (typeof onDismiss === "function") {
      onDismiss();
    }
  }, [isSetupRoute, mode, onDismiss]);

  const handleStart = async (): Promise<boolean> => {
    const industryId = selectedIndustry || industriesRef.current[0]?.id || "";
    const tenantGuid = resolvedTenantGuid;

    if (!industryId || !tenantGuid) {
      logger.warn("SignupPersonalizationStep: missing workspace context for finish setup", {
        accountGuid: normalizeProvisioningText(user?.accountGuid || user?.spAId),
        industryId,
        provisioningTenantId: normalizeProvisioningText(provisioning?.tenant_id),
        userTenantGuid: normalizeProvisioningText(user?.tenantGuid),
        workspaceGuid: normalizeProvisioningText(workspaceGuid)
      });
      setError(t("Workspace setup is still being prepared. Please try again in a moment."));
      return false;
    }

    setLoading(true);
    setPhase("apply_template");
    setError("");

    try {
      await applyIndustryTemplate(industryId);

      setPhase("update_tenant");
      await updateTenant(tenantGuid, { industry: industryId });

      if (useSampleData) {
        setPhase("start_sample_data");
        const template = industries.find((item) => item.id === industryId);
        const defaults = getTemplateDefaults(template);

        try {
          logger.info("Adding sample data to tenant with config: ", defaults);
          await addSampleDataToTenant({
            tenant_guid: tenantGuid,
            num_accounts: defaults.num_accounts || 15,
            num_customers_per_account: defaults.num_customers_per_account || 2,
            num_products: defaults.num_products || 5,
            num_users: defaults.num_users || 10,
            num_campaigns: defaults.num_campaigns || 5,
            num_opportunities: defaults.num_opportunities || 60,
            prev_quarter_activity_count: defaults.prev_quarter_activity_count || 200,
            curr_quarter_activity_count: defaults.curr_quarter_activity_count || 100,
            num_signals: defaults.num_signals || 20
          });
        } catch (err: any) {
          const message =
            err?.message ||
            err?.content?.error ||
            err?.content?.message ||
            t("Sample data generation failed. Try again or turn off sample data.");
          logger.error(message);
          setError(message);
          return false;
        }
      }

      const nextTenantConfig = getCompletedTenantConfig(user);
      const nextUser = {
        ...(user as unknown as User),
        industry: selectedIndustry,
        tenantGuid,
        tenantDisplayString: resolvedTenantDisplayString || user?.tenantDisplayString,
        pendingWorkspaceSelection: false,
        spPendingWorkspaceSelection: false,
        firstLogin: false,
        tenant_config: nextTenantConfig,
        ...(useSampleData
          ? { tenant_stats: { num_users: 6, num_opportunities: 25 } }
          : { tenant_stats: user?.tenant_stats })
      } as unknown as User;
      dispatch(
        updateAuthUser({
          user: nextUser
        })
      );

      try {
        await Promise.race([patchTenant(tenantGuid, { tenant_config: nextTenantConfig }), sleep(3000)]);
      } catch (err) {
        logger.error("Failed to persist personalization completion remotely", { err });
      }

      setPhase("refresh_dashboard");
      void refreshDashboardStats().catch((err) => {
        logger.warn("SignupPersonalizationStep: dashboard refresh failed", { err });
      });

      try {
        const completedKey = tenantGuid ? `onboarding_overlay_completed:${tenantGuid}` : "onboarding_overlay_completed";
        localStorage.setItem(completedKey, "true");
      } catch {
        // Ignore storage errors.
      }

      if (typeof onComplete === "function") {
        void Promise.resolve(onComplete()).catch((err) => {
          logger.error("SignupPersonalizationStep: onComplete handler failed", { err });
        });
      }
      return true;
    } catch (err: any) {
      setError(err?.message || t("Failed to initialize your workspace."));
      return false;
    } finally {
      setLoading(false);
      setPhase("idle");
    }
  };

  const handleSubmit = async () => {
    await handleStart();
  };

  if (mode === "dialog" && !shouldRender) {
    return null;
  }

  const content = (
    <Shell>
      <Stack spacing={3}>
        <ProvisioningWaitGuide visible={showProvisioningGuide} />
        <ProvisioningProgressPanel
          content={provisioning}
          progress={provisioningProgress}
          provisioningError={provisioningError}
          provisioningRefreshing={provisioningRefreshing}
          provisioningRetrying={provisioningRetrying}
          onRefreshProvisioning={onRefreshProvisioning}
          onRetryProvisioning={onRetryProvisioning}
        />
        <PersonalizationForm
          loading={loading}
          phase={phase}
          error={error}
          industries={industries}
          selectedIndustry={selectedIndustry}
          industriesReady={industriesReady}
          useSampleData={useSampleData}
          mode={mode}
          onSelectIndustry={setSelectedIndustry}
          onToggleSampleData={setUseSampleData}
          onSubmit={handleSubmit}
          onDismiss={onDismiss}
        />
      </Stack>
    </Shell>
  );

  if (mode === "dialog") {
    return (
      <Dialog
        open={shouldRender}
        onClose={onDismiss}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: "relative" }}>
          {onDismiss ? (
            <IconButton
              aria-label={t("Close")}
              data-testid="close-onboarding-overlay"
              onClick={onDismiss}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
                zIndex: 1
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        overflow: "hidden"
      }}
    >
      {content}
    </Paper>
  );
}
